/**
 * useUndoToast - Hook for undo-able actions with toast notifications
 *
 * Usage:
 * const { showUndo, showSuccess, showError } = useUndoToast();
 *
 * // For destructive action with undo:
 * showUndo('Position removed', () => {
 *   // undo logic
 * });
 *
 * // For confirmation:
 * showSuccess('AAPL added to watchlist');
 *
 * // For error:
 * showError('Failed to update portfolio', () => {
 *   // retry logic
 * });
 */
import { useCallback, useRef, useEffect } from 'react'
import { create } from 'zustand'

export interface UndoToastItem {
  id: string
  message: string
  type: 'undo' | 'success' | 'error'
  expiresAt: number
  onUndo?: () => void
  onRetry?: () => void
  timeoutId?: NodeJS.Timeout
}

interface UndoToastStore {
  toasts: UndoToastItem[]
  addToast: (toast: Omit<UndoToastItem, 'id'>) => string
  removeToast: (id: string) => void
  executeUndo: (id: string) => void
  executeRetry: (id: string) => void
  clearExpired: () => void
}

const UNDO_DURATION = 6000  // 6 seconds for undo
const SUCCESS_DURATION = 3000  // 3 seconds for success
const ERROR_DURATION = 8000  // 8 seconds for error

export const useUndoToastStore = create<UndoToastStore>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = `undo-toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

    // Set timeout to remove toast
    const timeoutId = setTimeout(() => {
      get().removeToast(id)
    }, toast.expiresAt - Date.now())

    const newToast: UndoToastItem = {
      ...toast,
      id,
      timeoutId,
    }

    set((state) => ({
      toasts: [...state.toasts, newToast]
    }))

    return id
  },

  removeToast: (id) => {
    const toast = get().toasts.find(t => t.id === id)
    if (toast?.timeoutId) {
      clearTimeout(toast.timeoutId)
    }
    set((state) => ({
      toasts: state.toasts.filter(t => t.id !== id)
    }))
  },

  executeUndo: (id) => {
    const toast = get().toasts.find(t => t.id === id)
    if (toast?.onUndo) {
      toast.onUndo()
    }
    get().removeToast(id)
  },

  executeRetry: (id) => {
    const toast = get().toasts.find(t => t.id === id)
    if (toast?.onRetry) {
      toast.onRetry()
    }
    get().removeToast(id)
  },

  clearExpired: () => {
    const now = Date.now()
    set((state) => ({
      toasts: state.toasts.filter(t => t.expiresAt > now)
    }))
  },
}))

export function useUndoToast() {
  const { addToast, removeToast, executeUndo, executeRetry } = useUndoToastStore()
  const pendingActions = useRef<Map<string, { action: () => Promise<void>, timeoutId: NodeJS.Timeout }>>(new Map())

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pendingActions.current.forEach(({ timeoutId }) => clearTimeout(timeoutId))
      pendingActions.current.clear()
    }
  }, [])

  /**
   * Show an undo toast for destructive actions
   * Returns a function to cancel the pending action
   */
  const showUndo = useCallback((
    message: string,
    onUndo: () => void,
    onConfirm?: () => Promise<void>
  ): (() => void) => {
    const expiresAt = Date.now() + UNDO_DURATION

    const toastId = addToast({
      message,
      type: 'undo',
      expiresAt,
      onUndo: () => {
        // Cancel pending action
        const pending = pendingActions.current.get(toastId)
        if (pending) {
          clearTimeout(pending.timeoutId)
          pendingActions.current.delete(toastId)
        }
        // Execute undo
        onUndo()
      },
    })

    // Schedule actual delete after toast expires
    if (onConfirm) {
      const timeoutId = setTimeout(async () => {
        pendingActions.current.delete(toastId)
        try {
          await onConfirm()
        } catch (error) {
          console.error('Action failed:', error)
        }
      }, UNDO_DURATION)

      pendingActions.current.set(toastId, { action: onConfirm, timeoutId })
    }

    return () => {
      // Cancel function
      const pending = pendingActions.current.get(toastId)
      if (pending) {
        clearTimeout(pending.timeoutId)
        pendingActions.current.delete(toastId)
      }
      removeToast(toastId)
    }
  }, [addToast, removeToast])

  /**
   * Show a success toast
   */
  const showSuccess = useCallback((message: string) => {
    const expiresAt = Date.now() + SUCCESS_DURATION
    return addToast({
      message,
      type: 'success',
      expiresAt,
    })
  }, [addToast])

  /**
   * Show an error toast with optional retry
   */
  const showError = useCallback((message: string, onRetry?: () => void) => {
    const expiresAt = Date.now() + ERROR_DURATION
    return addToast({
      message,
      type: 'error',
      expiresAt,
      onRetry,
    })
  }, [addToast])

  return {
    showUndo,
    showSuccess,
    showError,
    removeToast,
    executeUndo,
    executeRetry,
  }
}

export default useUndoToast
